import { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/ko";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TPC_URL from "../../../utils/TPC_URL";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 500,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

function StockInModal({ open, onClose, openSnackbar, setUpdateStatus }) {
  const { group_code, project_code } = useParams();
  const [items, setItems] = useState([]);
  const [formData, setFormData] = useState({
    item_name: "",
    quantity: "",
    date: dayjs(),
  });
  const [errors, setErrors] = useState({
    item_name: "",
    quantity: "",
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const itemRes = await axios.get(
          TPC_URL +
            `/api/item/getAllItems?group_code=${group_code}&project_code=${project_code}`
        );
        setItems(itemRes.data || []);
      } catch (e) {
        console.log(e);
        openSnackbar("데이터를 불러오지 못했습니다.", "error");
      }
    }
    if (open) {
      fetchData();
    }
  }, [open, group_code, project_code, openSnackbar]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    validateField(name, value);
  };

  const handleItemChange = (event, newValue) => {
    setFormData({
      ...formData,
      item_name: newValue ? newValue.item_name : "",
      item_code: newValue ? newValue.item_code : "",
    });
    validateField("item_name", newValue ? newValue.item_name : "");
  };

  const handleDateChange = (newDate) => {
    setFormData({
      ...formData,
      date: newDate,
    });
  };

  const validateField = (name, value) => {
    let errorMessage = "";
    switch (name) {
      case "item_name":
        errorMessage = value ? "" : "제품명을 선택해주세요.";
        break;
      case "quantity":
        if (!value) {
          errorMessage = "수량을 입력해주세요.";
        } else if (
          isNaN(value) ||
          parseInt(value) <= 0 ||
          !Number.isInteger(Number(value))
        ) {
          errorMessage = "수량은 0보다 큰 정수여야 합니다.";
        }
        break;
      default:
        break;
    }
    setErrors({
      ...errors,
      [name]: errorMessage,
    });

    return errorMessage === "";
  };

  const validateForm = () => {
    const validItem = validateField("item_name", formData.item_name);
    const validQuantity = validateField("quantity", formData.quantity);
    return validItem && validQuantity;
  };

  const handleSubmit = async () => {
    try {
      if (validateForm()) {
        const param = {
          group_code,
          project_code,
          item_name: formData.item_name,
          qty: parseInt(formData.quantity),
          date: formData.date.format("YYYY-MM-DD HH:mm"),
        };
        const res = await axios.post(TPC_URL + "/api/stock/postStockIn", param);
        if (res.status === 200) {
          openSnackbar("입고 정보가 등록되었습니다.", "success");
          handleClose();
          setUpdateStatus((prev) => !prev);
        }
      } else {
        openSnackbar("입력 내용을 확인해주세요.", "error");
      }
    } catch (e) {
      console.log(e);
      openSnackbar("오류가 발생했습니다.", "error");
    }
  };

  const handleClose = () => {
    setFormData({
      item_name: "",
      quantity: "",
      date: dayjs(),
    });
    setErrors({
      item_name: "",
      quantity: "",
    });
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="stock-in-modal-title"
      aria-describedby="stock-in-modal-description"
    >
      <Box sx={modalStyle}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography id="stock-in-modal-title" variant="h6" component="h2">
            입고 등록
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Grid container spacing={2}>
          {/* 제품명 */}
          <Grid item xs={12}>
            <Autocomplete
              id="item-select"
              options={items}
              getOptionLabel={(option) =>
                `${option.material_grouping} - ${option.item_name}`
              }
              onChange={handleItemChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="제품명"
                  name="item_name"
                  error={!!errors.item_name}
                  helperText={errors.item_name}
                  fullWidth
                  required
                />
              )}
            />
          </Grid>

          {/* 수량 */}
          <Grid item xs={12}>
            <TextField
              label="수량"
              name="quantity"
              type="number"
              value={formData.quantity}
              onChange={handleChange}
              error={!!errors.quantity}
              helperText={errors.quantity}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">EA</InputAdornment>
                ),
              }}
              fullWidth
              required
              inputProps={{
                min: 1,
                step: 1,
              }}
            />
          </Grid>

          {/* 입고 일시 */}
          <Grid item xs={12}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
              <DateTimePicker
                label="입고 일시"
                value={formData.date}
                onChange={handleDateChange}
                format="YYYY-MM-DD HH:mm"
                ampm={false}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          {/* 버튼 영역 */}
          <Grid item xs={12} mt={2} display="flex" justifyContent="flex-end">
            <Button variant="outlined" onClick={handleClose} sx={{ mr: 1 }}>
              취소
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{
                bgcolor: "#1976d2",
                "&:hover": {
                  bgcolor: "#1565c0",
                },
              }}
            >
              등록
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
}

export default StockInModal;
